import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, X } from 'lucide-react';
import { FeedbackFlow } from '../Feedback/FeedbackFlow';
interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestone?: number | null;
  milestoneType?: 'scan' | 'rewrite' | null;
}
export function FeedbackModal({
  isOpen,
  onClose,
  milestone = null,
  milestoneType = 'scan'
}: FeedbackModalProps) {
  const milestoneLabel = milestone ? milestoneType === 'rewrite' ? `Rewrite ${milestone} complete` : `Scan ${milestone} complete` : 'Share your story';
  const isFirstScan = milestone === 1 && milestoneType === 'scan';
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-2368" data-mp="qa6au3blw5t4">
      {isOpen && <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-2369" data-mp="b9zq5juqedx1">
          <motion.button type="button" aria-label="Close feedback modal" className="absolute inset-0 cursor-default bg-black/85 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-2370" data-mp="bccfwj6qdskn" />

          <motion.section role="dialog" aria-modal="true" aria-labelledby="smart-feedback-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto border border-red-500/40 bg-[#050505] p-6 shadow-2xl sm:p-8" data-id="element-2371" data-mp="z7niel9hf5dd">
            <div className="absolute left-0 top-0 h-1 w-full bg-[#FF0000]" data-id="element-2372" data-mp="3nrmdr73cmy28" />
            <div className="pointer-events-none absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-[#FF0000]" data-id="element-2373" data-mp="3j0w9xyakn39n" />
            <div className="pointer-events-none absolute right-0 top-0 h-6 w-6 border-r-2 border-t-2 border-[#FF0000]" data-id="element-2374" data-mp="1jd2sfu6mwvv" />
            <div className="pointer-events-none absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2375" data-mp="3spnyrxkc57gc" />
            <div className="pointer-events-none absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2376" data-mp="aiqezg9oxzka" />

            <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded p-2 text-gray-500 transition-colors duration-150 ease-out hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FF0000]" aria-label="Close feedback modal" data-id="element-2377" data-mp="2cc7m8itwsyei">
              <X size={20} data-id="element-2378" data-mp="228iqo44ktslo" />
            </button>

            <header className="mb-7 pr-10" data-id="element-2379" data-mp="22ad9ncwafan4">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-red-500/40 bg-red-500/10" data-id="element-2380" data-mp="1ohnpzf48eht6">
                <MessageSquare className="text-[#FF0000]" size={20} aria-hidden="true" data-id="element-2381" data-mp="1tee4vz4av7d0" />
              </div>
              <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF0000]" data-id="element-2382" data-mp="1jiu87ahww4n7">
                {milestoneLabel}
              </p>
              <h2 id="smart-feedback-title" className="text-2xl font-black uppercase tracking-tight text-white" data-id="element-2383" data-mp="1ah32w6ra9ztv">
                {isFirstScan ? 'How did your first scan go?' : 'How was your experience?'}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-2384" data-mp="14sme39vybayr">
                {isFirstScan ? 'Rate your results and leave a quick review — it helps others find clarity too.' : 'Your honest feedback helps us make the experience clearer and more useful.'}
              </p>
            </header>

            <FeedbackFlow idPrefix="smart-feedback" contextLabel={milestone ? milestoneLabel : null} onTrustpilotClick={onClose} data-id="element-2385" data-mp="qsbo092gu0en" />
          </motion.section>
        </div>}
    </AnimatePresence>;
}